import { useState, useEffect } from 'react';
import { getNotifications, getWatchers, deleteNotification, clearNotifications, type NotificationLog, type Watcher, type PaginatedNotifications } from '../lib/api';

const PAGE_SIZE = 50;

const CABIN_LABELS: Record<string, string> = {
  economy: 'Economy',
  premium_economy: 'Premium Economy',
  business: 'Business',
  first: 'First',
};

interface FlightDetails {
  date: string;
  origin: string;
  destination: string;
  cabin: string;
  program: string;
  points: number;
  taxes: number;
  taxesCurrency: string;
  remainingSeats: number;
  priceUsd: number;
}

function parseFlightDetails(json: string): FlightDetails | null {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function formatPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatPoints(points: number): string {
  return new Intl.NumberFormat('en-US').format(points);
}

export function HistoryPage() {
  const [notifications, setNotifications] = useState<NotificationLog[]>([]);
  const [watchers, setWatchers] = useState<Record<string, Watcher>>({});
  const [selectedWatcher, setSelectedWatcher] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  useEffect(() => {
    const loadData = async () => {
      try {
        const offset = (currentPage - 1) * PAGE_SIZE;
        const [notificationData, watcherList] = await Promise.all([
          getNotifications(selectedWatcher || undefined, PAGE_SIZE, offset),
          getWatchers(),
        ]);

        setNotifications(notificationData.items);
        setTotalItems(notificationData.total);
        setWatchers(Object.fromEntries(watcherList.map(w => [w.id, w])));
      } catch (error) {
        console.error('Failed to load history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedWatcher, currentPage]);

  const watcherList = Object.values(watchers);

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
      setNotifications(notifications.filter(n => n.id !== id));
      setTotalItems(t => t - 1);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to clear all notification history?')) {
      return;
    }
    try {
      await clearNotifications(selectedWatcher || undefined);
      setNotifications([]);
      setTotalItems(0);
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold">Notification History</h2>

        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="flex-1 sm:flex-none sm:w-48 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500"
            value={selectedWatcher}
            onChange={e => {
              setSelectedWatcher(e.target.value);
              setCurrentPage(1);
              setLoading(true);
            }}
          >
            <option value="">All Watchers</option>
            {watcherList.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          {notifications.length > 0 && (
            <button
              className="inline-flex items-center justify-center px-3 md:px-4 py-2 text-sm font-medium rounded-lg bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors cursor-pointer border-none whitespace-nowrap"
              onClick={handleClearAll}
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-12 text-center">
          <p className="text-gray-500">No notifications sent yet.</p>
        </div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {notifications.map(notification => {
              const details = parseFlightDetails(notification.flightDetails);
              const watcher = watchers[notification.watcherId];

              return (
                <div key={notification.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200">
                        {watcher?.name || 'Unknown'}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        {details ? CABIN_LABELS[details.cabin] || details.cabin : '-'}
                      </span>
                    </div>
                    <button
                      className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 rounded cursor-pointer border-none bg-transparent"
                      onClick={() => handleDelete(notification.id)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="text-base font-medium mb-1">
                    {details ? `${details.origin} → ${details.destination}` : '-'}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">
                    {details?.date || '-'} · {details?.program || '-'}
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span>{formatPoints(notification.lastNotifiedPoints)} pts</span>
                    <span className="font-medium text-green-600">{formatPrice(notification.lastNotifiedPriceUsd)}</span>
                    <span className="text-gray-500">{details?.remainingSeats || '-'} seats</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {new Date(notification.lastNotifiedAt).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Time</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Watcher</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Route</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Date</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Cabin</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Program</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Points</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Price</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200">Seats</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold border-b border-gray-200"></th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map(notification => {
                    const details = parseFlightDetails(notification.flightDetails);
                    const watcher = watchers[notification.watcherId];

                    return (
                      <tr key={notification.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm whitespace-nowrap border-b border-gray-100">
                          {new Date(notification.lastNotifiedAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200">
                            {watcher?.name || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-medium border-b border-gray-100">
                          {details ? `${details.origin} → ${details.destination}` : '-'}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100">{details?.date || '-'}</td>
                        <td className="px-3 py-3 border-b border-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {details ? CABIN_LABELS[details.cabin] || details.cabin : '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-sm border-b border-gray-100">
                          {details?.program || '-'}
                        </td>
                        <td className="px-3 py-3 font-medium border-b border-gray-100">
                          {formatPoints(notification.lastNotifiedPoints)}
                        </td>
                        <td className="px-3 py-3 font-medium text-green-600 border-b border-gray-100">
                          {formatPrice(notification.lastNotifiedPriceUsd)}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100">
                          {details?.remainingSeats || '-'}
                        </td>
                        <td className="px-3 py-3 border-b border-gray-100">
                          <button
                            className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 rounded cursor-pointer border-none bg-transparent"
                            onClick={() => handleDelete(notification.id)}
                            title="Delete"
                          >
                            &times;
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
              <div className="text-sm text-gray-500">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
